import { useEffect, useState, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User } from '@supabase/supabase-js';

export function useAdmin() {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const checkedRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const checkAdmin = async (u: User | null) => {
      if (!mounted) return;
      if (!u) {
        setUser(null);
        setIsAdmin(false);
        setLoading(false);
        return;
      }
      setUser(u);
      try {
        // Small delay to ensure auth token is propagated
        await new Promise(r => setTimeout(r, 100));
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', u.id)
          .eq('role', 'admin');
        
        if (!mounted) return;
        
        if (error) {
          console.error('Role check error:', error.message);
          setIsAdmin(false);
        } else {
          const admin = !!(data && data.length > 0);
          console.log('Admin check result:', admin, 'roles:', data);
          setIsAdmin(admin);
        }
      } catch (err) {
        if (!mounted) return;
        console.error('Role check failed:', err);
        setIsAdmin(false);
      }
      if (mounted) setLoading(false);
    };

    // Get initial session first
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      checkedRef.current = true;
      checkAdmin(session?.user ?? null);
    });

    // Listen for changes after initial check
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!mounted || !checkedRef.current) return;
        setLoading(true);
        checkAdmin(session?.user ?? null);
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
  };

  return { user, isAdmin, loading, signOut };
}
